import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { SchemaGraph, SchemaNode, SchemaEdge, extractMetadata } from './graphLoader';
import { getTypeColor } from './theme';
import { docProcessingApi, DocumentInfo } from '../../services/docProcessingApi';
import { getAuthHeaders } from '../../services/apiClient';
import { SchemaNodeListPanel } from './SchemaNodeListPanel';

// ── Color palette for databases (up to 22 distinct colors) ───────────────

const DB_COLORS = [
  '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#14b8a6',
  '#a855f7', '#eab308', '#22c55e', '#e11d48', '#0ea5e9',
  '#d946ef', '#f472b6', '#fb923c', '#4ade80', '#2dd4bf',
  '#818cf8', '#fbbf24',
];

const NODE_TYPE_LABELS: Record<string, string> = {
  Database: 'Databases',
  SQLTable: 'Tables',
  Module: 'Modules',
};

function getDbColor(dbName: string, dbIndex: Map<string, number>): string {
  if (!dbIndex.has(dbName)) {
    dbIndex.set(dbName, dbIndex.size);
  }
  return DB_COLORS[dbIndex.get(dbName)! % DB_COLORS.length];
}

// ── Force simulation types ───────────────────────────────────────────────

interface SimNode {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  type: string;
  name: string;
  database?: string;
  schema?: string;
  description?: string;
  columnCount?: number;
  color: string;
  radius: number;
  pinned?: boolean; // when true, skip force updates (user-dragged)
  labelDir?: 'top' | 'bottom' | 'left' | 'right'; // precomputed label placement
  dashed?: boolean; // to-be-created entity (CR impact graph): dotted outline
}

interface SimEdge {
  source: string;
  target: string;
  type: string;
  sourceColumn?: string;
  targetColumn?: string;
  indirect?: boolean; // shared-table relationship (CR impact graph)
  description?: string;
}

// ── Web Worker for force simulation ──────────────────────────────────────

const WORKER_CODE = `
self.onmessage = function(evt) {
  var data = evt.data;
  var nodes = data.nodes;
  var edges = data.edges;
  var width = data.width;
  var height = data.height;
  var iterations = data.iterations || 150;

  var nodeMap = {};
  for (var i = 0; i < nodes.length; i++) nodeMap[nodes[i].id] = nodes[i];

  // Compute collision radius: circle radius + label-proportional padding
  // ~6px per character at 9px font, halved for center-aligned text
  for (var i = 0; i < nodes.length; i++) {
    var n = nodes[i];
    var labelHalfW = Math.min(n.nameLen || 6, 18) * 3;
    n.cr = Math.max(n.radius + 8, labelHalfW);
  }

  // Initialize positions in a circle
  var cx = width / 2;
  var cy = height / 2;
  var initRadius = Math.min(width, height) * 0.35;
  for (var i = 0; i < nodes.length; i++) {
    var angle = (2 * Math.PI * i) / nodes.length;
    nodes[i].x = cx + initRadius * Math.cos(angle) + (Math.random() - 0.5) * 50;
    nodes[i].y = cy + initRadius * Math.sin(angle) + (Math.random() - 0.5) * 50;
    nodes[i].vx = 0;
    nodes[i].vy = 0;
  }

  var alpha0 = 1.0;
  var alphaDecay = 1 - Math.pow(0.001, 1 / iterations);

  // Spatial grid parameters
  var cellSize = 120;

  for (var iter = 0; iter < iterations; iter++) {
    var alpha = alpha0 * Math.pow(1 - alphaDecay, iter);
    if (alpha < 0.001) break;

    // Build spatial grid for O(n) repulsion
    var grid = {};
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var gx = Math.floor(n.x / cellSize);
      var gy = Math.floor(n.y / cellSize);
      var key = gx + ',' + gy;
      if (!grid[key]) grid[key] = [];
      grid[key].push(i);
    }

    // Repulsion (charge force) — only check neighboring cells
    for (var i = 0; i < nodes.length; i++) {
      var a = nodes[i];
      var gx = Math.floor(a.x / cellSize);
      var gy = Math.floor(a.y / cellSize);
      for (var dx = -1; dx <= 1; dx++) {
        for (var dy = -1; dy <= 1; dy++) {
          var key = (gx + dx) + ',' + (gy + dy);
          var cell = grid[key];
          if (!cell) continue;
          for (var ci = 0; ci < cell.length; ci++) {
            var j = cell[ci];
            if (j <= i) continue;
            var b = nodes[j];
            var ddx = b.x - a.x;
            var ddy = b.y - a.y;
            var dist = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
            var strength = -400 * alpha / (dist * dist);
            var fx = ddx / dist * strength;
            var fy = ddy / dist * strength;
            a.vx -= fx;
            a.vy -= fy;
            b.vx += fx;
            b.vy += fy;
          }
        }
      }
    }

    // Collision force — prevent overlap using collision radii
    // For large graphs, skip every other iteration to save time
    if (nodes.length < 800 || iter % 2 === 0)
    for (var i = 0; i < nodes.length; i++) {
      var a = nodes[i];
      var gx = Math.floor(a.x / cellSize);
      var gy = Math.floor(a.y / cellSize);
      for (var dx = -1; dx <= 1; dx++) {
        for (var dy = -1; dy <= 1; dy++) {
          var key = (gx + dx) + ',' + (gy + dy);
          var cell = grid[key];
          if (!cell) continue;
          for (var ci = 0; ci < cell.length; ci++) {
            var j = cell[ci];
            if (j <= i) continue;
            var b = nodes[j];
            var ddx = b.x - a.x;
            var ddy = b.y - a.y;
            var dist = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
            var minDist = a.cr + b.cr;
            if (dist < minDist) {
              var overlap = (minDist - dist) * 0.5;
              var ox = (ddx / dist) * overlap;
              var oy = (ddy / dist) * overlap;
              a.vx -= ox;
              a.vy -= oy;
              b.vx += ox;
              b.vy += oy;
            }
          }
        }
      }
    }

    // Attraction (link force)
    for (var ei = 0; ei < edges.length; ei++) {
      var edge = edges[ei];
      var a = nodeMap[edge.source];
      var b = nodeMap[edge.target];
      if (!a || !b) continue;
      var ddx = b.x - a.x;
      var ddy = b.y - a.y;
      var dist = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
      var targetDist = 120;
      var strength = (dist - targetDist) * 0.03 * alpha;
      var fx = ddx / dist * strength;
      var fy = ddy / dist * strength;
      a.vx += fx;
      a.vy += fy;
      b.vx -= fx;
      b.vy -= fy;
    }

    // Center force
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].vx += (cx - nodes[i].x) * 0.01 * alpha;
      nodes[i].vy += (cy - nodes[i].y) * 0.01 * alpha;
    }

    // Apply velocity with damping
    var maxV = 0;
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].vx *= 0.6;
      nodes[i].vy *= 0.6;
      nodes[i].x += nodes[i].vx;
      nodes[i].y += nodes[i].vy;
      var v = Math.abs(nodes[i].vx) + Math.abs(nodes[i].vy);
      if (v > maxV) maxV = v;
    }
    // Early exit when stable
    if (maxV < 0.5) break;
  }

  // Return positioned nodes (just id, x, y)
  var positions = [];
  for (var i = 0; i < nodes.length; i++) {
    positions.push({ id: nodes[i].id, x: nodes[i].x, y: nodes[i].y });
  }
  self.postMessage({ positions: positions });
};
`;

let workerBlobUrl: string | null = null;
function getWorkerUrl(): string {
  if (!workerBlobUrl) {
    workerBlobUrl = URL.createObjectURL(new Blob([WORKER_CODE], { type: 'application/javascript' }));
  }
  return workerBlobUrl;
}

// ── Canvas renderer ─────────────────────────────────────────────────────

function drawGraph(
  ctx: CanvasRenderingContext2D,
  nodes: SimNode[],
  edges: SimEdge[],
  nodeMap: Map<string, SimNode>,
  transform: { x: number; y: number; k: number },
  hoveredNode: SimNode | null,
  selectedNode: SimNode | null,
  width: number,
  height: number,
  labelsOn: boolean,
) {
  ctx.clearRect(0, 0, width, height);
  ctx.save();
  ctx.translate(transform.x, transform.y);
  ctx.scale(transform.k, transform.k);

  // Viewport bounds in graph coordinates for culling
  const invK = 1 / transform.k;
  const vLeft = -transform.x * invK - 50;
  const vTop = -transform.y * invK - 50;
  const vRight = (width - transform.x) * invK + 50;
  const vBottom = (height - transform.y) * invK + 50;

  // Pre-compute connected set for selected node
  const connectedIds = new Set<string>();
  if (selectedNode) {
    for (const e of edges) {
      if (e.source === selectedNode.id) connectedIds.add(e.target);
      if (e.target === selectedNode.id) connectedIds.add(e.source);
    }
  }

  // Draw edges — batched by style to minimize state changes
  const highlightedEdges: SimEdge[] = [];
  const moduleEdges: SimEdge[] = [];
  const highlightedModuleEdges: SimEdge[] = [];
  const indirectEdges: SimEdge[] = [];

  // Batch non-highlighted edges (separate module_contains for dashed style)
  ctx.beginPath();
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1.2;
  ctx.globalAlpha = selectedNode ? 0.25 : 0.7;
  for (const edge of edges) {
    const src = nodeMap.get(edge.source);
    const tgt = nodeMap.get(edge.target);
    if (!src || !tgt) continue;
    // Viewport culling: skip edges entirely outside viewport
    if ((src.x < vLeft && tgt.x < vLeft) || (src.x > vRight && tgt.x > vRight) ||
        (src.y < vTop && tgt.y < vTop) || (src.y > vBottom && tgt.y > vBottom)) continue;
    if (selectedNode && (edge.source === selectedNode.id || edge.target === selectedNode.id)) {
      if (edge.type === 'module_contains') {
        highlightedModuleEdges.push(edge);
      } else {
        highlightedEdges.push(edge);
      }
      continue;
    }
    if (edge.type === 'module_contains') {
      moduleEdges.push(edge);
      continue;
    }
    // Indirect (shared-table) relationships render dashed + lighter, so a
    // real dependency and a "both touch the same table" never look alike.
    if (edge.indirect) {
      indirectEdges.push(edge);
      continue;
    }
    ctx.moveTo(src.x, src.y);
    ctx.lineTo(tgt.x, tgt.y);
  }
  ctx.stroke();

  // Non-highlighted indirect edges (dashed, light slate)
  if (indirectEdges.length > 0) {
    ctx.beginPath();
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    for (const edge of indirectEdges) {
      const src = nodeMap.get(edge.source)!;
      const tgt = nodeMap.get(edge.target)!;
      ctx.moveTo(src.x, src.y);
      ctx.lineTo(tgt.x, tgt.y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Draw non-highlighted module_contains edges (dashed purple)
  if (moduleEdges.length > 0) {
    ctx.beginPath();
    ctx.strokeStyle = '#a78bfa';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    for (const edge of moduleEdges) {
      const src = nodeMap.get(edge.source)!;
      const tgt = nodeMap.get(edge.target)!;
      ctx.moveTo(src.x, src.y);
      ctx.lineTo(tgt.x, tgt.y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.globalAlpha = 1;

  // Batch highlighted edges
  if (highlightedEdges.length > 0) {
    // Indirect edges stay dashed even when highlighted — selection must not
    // promote a shared-table relationship into looking like a direct one.
    ctx.beginPath();
    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 3]);
    for (const edge of highlightedEdges) {
      if (!edge.indirect) continue;
      const src = nodeMap.get(edge.source)!;
      const tgt = nodeMap.get(edge.target)!;
      ctx.moveTo(src.x, src.y);
      ctx.lineTo(tgt.x, tgt.y);
    }
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.beginPath();
    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 2.5;
    for (const edge of highlightedEdges) {
      if (edge.indirect) continue;
      const src = nodeMap.get(edge.source)!;
      const tgt = nodeMap.get(edge.target)!;
      ctx.moveTo(src.x, src.y);
      ctx.lineTo(tgt.x, tgt.y);
    }
    ctx.stroke();

    // Edge labels for highlighted FK relationships (only when zoomed in)
    if (transform.k > 0.6) {
      ctx.font = '8px Inter, sans-serif';
      ctx.fillStyle = '#6366f1';
      ctx.textAlign = 'center';
      for (const edge of highlightedEdges) {
        if (edge.type === 'contains' || !edge.sourceColumn) continue;
        const src = nodeMap.get(edge.source)!;
        const tgt = nodeMap.get(edge.target)!;
        ctx.fillText(`${edge.sourceColumn}\u2192${edge.targetColumn}`, (src.x + tgt.x) / 2, (src.y + tgt.y) / 2 - 4);
      }
    }
  }

  // Highlighted module_contains edges (dashed, thicker purple)
  if (highlightedModuleEdges.length > 0) {
    ctx.beginPath();
    ctx.strokeStyle = '#8b5cf6';
    ctx.lineWidth = 3;
    ctx.setLineDash([8, 4]);
    for (const edge of highlightedModuleEdges) {
      const src = nodeMap.get(edge.source)!;
      const tgt = nodeMap.get(edge.target)!;
      ctx.moveTo(src.x, src.y);
      ctx.lineTo(tgt.x, tgt.y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Draw nodes — with viewport culling.
  // Labels toggle: ON = full names at every zoom level, OFF = none
  // (Database anchors keep theirs either way).
  const showLabels = labelsOn;
  const dbFont = 'bold 11px Inter, sans-serif';
  const tableFont = '9px Inter, sans-serif';

  for (const node of nodes) {
    // Viewport culling
    if (node.x < vLeft || node.x > vRight || node.y < vTop || node.y > vBottom) continue;

    const isSelected = selectedNode?.id === node.id;
    const isHovered = hoveredNode?.id === node.id;
    const isConnected = selectedNode && connectedIds.has(node.id);

    // Dim unrelated nodes when something is selected
    if (selectedNode && !isSelected && !isConnected) {
      ctx.globalAlpha = 0.15;
    }

    // Node circle. Dashed = "to be created" (CR impact graph): hollow fill
    // with a dotted ring in the type color, so new entities read as planned,
    // not existing.
    ctx.beginPath();
    ctx.arc(node.x, node.y, node.radius, 0, 2 * Math.PI);
    if (node.dashed) {
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.fill();
      ctx.setLineDash([4, 3]);
      ctx.strokeStyle = node.color;
      ctx.lineWidth = isSelected || isHovered ? 3 : 2;
      ctx.stroke();
      ctx.setLineDash([]);
    } else {
      ctx.fillStyle = node.color;
      ctx.fill();
      if (isSelected || isHovered) {
        ctx.strokeStyle = '#111827';
        ctx.lineWidth = 3;
        ctx.stroke();
      } else {
        ctx.strokeStyle = 'rgba(255,255,255,0.8)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    }

    // Node label — use precomputed direction (no O(n²) lookup)
    if (showLabels || node.type === 'Database') {
      ctx.font = node.type === 'Database' ? dbFont : tableFont;
      ctx.fillStyle = '#111827';
      // Full names when labels are on \u2014 a truncated node name in an impact
      // graph is unreadable ("Commercial.dbo.m\u2026" told Anthony nothing).
      const label = node.name;
      const pad = node.radius + 4;
      const dir = node.labelDir || 'bottom';

      if (dir === 'left') {
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, node.x - pad, node.y);
      } else if (dir === 'right') {
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, node.x + pad, node.y);
      } else if (dir === 'top') {
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(label, node.x, node.y - pad);
      } else {
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(label, node.x, node.y + pad);
      }
    }

    ctx.globalAlpha = 1;
  }

  ctx.restore();
}

// Precompute label placement direction for all nodes using spatial grid (O(n))
function precomputeLabelDirs(nodes: SimNode[]) {
  const cellSize = 100;
  const grid = new Map<string, number[]>();
  for (let i = 0; i < nodes.length; i++) {
    const gx = Math.floor(nodes[i].x / cellSize);
    const gy = Math.floor(nodes[i].y / cellSize);
    const key = `${gx},${gy}`;
    const arr = grid.get(key);
    if (arr) arr.push(i); else grid.set(key, [i]);
  }
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const gx = Math.floor(node.x / cellSize);
    const gy = Math.floor(node.y / cellSize);
    let avgDx = 0, avgDy = 0, count = 0;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const cell = grid.get(`${gx + dx},${gy + dy}`);
        if (!cell) continue;
        for (const j of cell) {
          if (j === i) continue;
          const ox = nodes[j].x - node.x;
          const oy = nodes[j].y - node.y;
          const d2 = ox * ox + oy * oy;
          if (d2 < 10000) { avgDx += ox; avgDy += oy; count++; }
        }
      }
    }
    if (count === 0) { node.labelDir = 'bottom'; }
    else if (Math.abs(avgDx) > Math.abs(avgDy)) {
      node.labelDir = avgDx > 0 ? 'left' : 'right';
    } else {
      node.labelDir = avgDy > 0 ? 'top' : 'bottom';
    }
  }
}

// Spatial grid for O(1) hit testing — stored in a ref, rebuilt after layout
let _hitGrid: Map<string, SimNode[]> = new Map();
let _hitCellSize = 60;

function buildHitGrid(nodes: SimNode[]) {
  _hitGrid = new Map();
  _hitCellSize = 60;
  for (const n of nodes) {
    // Add node to all cells its radius touches
    const r = n.radius + 4;
    const x0 = Math.floor((n.x - r) / _hitCellSize);
    const x1 = Math.floor((n.x + r) / _hitCellSize);
    const y0 = Math.floor((n.y - r) / _hitCellSize);
    const y1 = Math.floor((n.y + r) / _hitCellSize);
    for (let gx = x0; gx <= x1; gx++) {
      for (let gy = y0; gy <= y1; gy++) {
        const key = `${gx},${gy}`;
        const arr = _hitGrid.get(key);
        if (arr) arr.push(n); else _hitGrid.set(key, [n]);
      }
    }
  }
}

function hitTestGrid(x: number, y: number): SimNode | null {
  const gx = Math.floor(x / _hitCellSize);
  const gy = Math.floor(y / _hitCellSize);
  const cell = _hitGrid.get(`${gx},${gy}`);
  if (!cell) return null;
  // Reverse order so top-rendered nodes are found first
  for (let i = cell.length - 1; i >= 0; i--) {
    const n = cell[i];
    const dx = n.x - x;
    const dy = n.y - y;
    if (dx * dx + dy * dy < (n.radius + 4) * (n.radius + 4)) return n;
  }
  return null;
}

// ── Legend component ────────────────────────────────────────────────────

function Legend({
  dbCounts,
  dbColors,
  filter,
  onToggle,
}: {
  dbCounts: Map<string, number>;
  dbColors: Map<string, string>;
  filter: Set<string>;
  onToggle: (db: string) => void;
}) {
  return (
    <div style={{
      position: 'absolute', top: 94, right: 16, zIndex: 10,
      background: 'rgba(255,255,255,0.95)', borderRadius: 10,
      padding: '10px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
      maxHeight: 'calc(100% - 100px)', overflowY: 'auto',
      fontSize: 12,
    }}>
      {Array.from(dbCounts.entries()).map(([db, count]) => (
        <div
          key={db}
          onClick={() => onToggle(db)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 8px', borderRadius: 16, marginBottom: 3,
            cursor: 'pointer',
            background: dbColors.get(db) + '20',
            opacity: filter.size === 0 || filter.has(db) ? 1 : 0.3,
            border: `2px solid ${dbColors.get(db)}`,
          }}
        >
          <span style={{ fontWeight: 600, color: dbColors.get(db), minWidth: 50 }}>
            {count}
          </span>
          <span style={{ color: '#374151' }}>{db}</span>
        </div>
      ))}
    </div>
  );
}

// ── Detail tooltip ──────────────────────────────────────────────────────

function NodeDetail({
  node,
  rawNode,
  connectedNodes,
  onClose,
}: {
  node: SimNode;
  rawNode?: SchemaNode;
  connectedNodes: SimNode[];
  onClose: () => void;
}) {
  return (
    <div style={{
      position: 'absolute', bottom: 44, left: 16, zIndex: 10,
      background: 'white', borderRadius: 12, padding: 20,
      boxShadow: '0 4px 24px rgba(0,0,0,0.15)', maxWidth: 420,
      maxHeight: '50vh', overflowY: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 600, color: node.color, textTransform: 'uppercase' }}>
            {node.database || node.type} {node.schema ? `\u00b7 ${node.schema}` : ''}
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{node.name}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>{'\u00d7'}</button>
      </div>
      {node.description && (
        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>{node.description}</p>
      )}
      {node.columnCount != null && (
        <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8 }}>{node.columnCount} columns</div>
      )}
      {rawNode?.columns && rawNode.columns.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#374151', marginBottom: 4, textTransform: 'uppercase' }}>
            Columns ({rawNode.columns.length})
          </div>
          {/* All columns, scrollable \u2014 a hard slice(0, 20) here silently hid
              92 of actualschedule's 112 columns with no indicator. */}
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #f3f4f6', borderRadius: 4 }}>
            <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
              <tbody>
                {rawNode.columns.map((col) => (
                  <tr key={col.name} style={{ borderBottom: '1px solid #f3f4f6' }} title={col.description || undefined}>
                    <td style={{ padding: '2px 4px', fontWeight: rawNode.primaryKey?.includes(col.name) ? 600 : 400 }}>
                      {rawNode.primaryKey?.includes(col.name) ? '\ud83d\udd11 ' : ''}{col.name}
                    </td>
                    <td style={{ padding: '2px 4px', color: '#6b7280' }}>{col.dataType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {connectedNodes.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#374151', marginBottom: 4, textTransform: 'uppercase' }}>
            Connected ({connectedNodes.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {connectedNodes.slice(0, 15).map((cn, idx) => (
              <span key={`${cn.id}-${idx}`} style={{
                fontSize: 10, padding: '2px 6px', borderRadius: 8,
                background: cn.color + '20', color: cn.color, border: `1px solid ${cn.color}40`,
              }}>
                {cn.name}
              </span>
            ))}
            {connectedNodes.length > 15 && (
              <span style={{ fontSize: 10, padding: '2px 6px', color: '#9ca3af' }}>
                +{connectedNodes.length - 15} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Type filter chips ───────────────────────────────────────────────────

function TypeFilterChips({
  typeCounts,
  filter,
  onToggle,
}: {
  typeCounts: Map<string, number>;
  filter: Set<string>;
  onToggle: (type: string) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {Array.from(typeCounts.entries()).map(([type, count]) => {
        const active = filter.size === 0 || filter.has(type);
        const label = NODE_TYPE_LABELS[type] || type;
        return (
          <button
            key={type}
            onClick={() => onToggle(type)}
            style={{
              padding: '3px 10px', borderRadius: 12, fontSize: 11, cursor: 'pointer',
              border: '1px solid ' + (active ? '#6366f1' : '#d1d5db'),
              background: active ? '#eef2ff' : '#f9fafb',
              color: active ? '#4338ca' : '#6b7280',
              fontWeight: active ? 600 : 400,
            }}
          >
            {label} ({count})
          </button>
        );
      })}
    </div>
  );
}

// ── Typeahead ComboFilter ─────────────────────────────────────────────

function ComboFilter({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (val: string) => void;
}) {
  const [inputText, setInputText] = useState(value);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Sync external value changes
  useEffect(() => {
    setInputText(value);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    if (!inputText.trim()) return options;
    const q = inputText.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, inputText]);

  const handleSelect = (opt: string) => {
    onChange(opt);
    setInputText(opt);
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setInputText('');
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputText(val);
    setOpen(true);
    // Only apply filter when typed text exactly matches an option;
    // otherwise wait for the user to select from the dropdown.
    if (!val.trim()) {
      onChange('');
    } else if (options.some((o) => o.toLowerCase() === val.toLowerCase())) {
      onChange(val);
    }
  };

  const isActive = value.length > 0;

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${isActive ? '#818cf8' : '#d1d5db'}`, borderRadius: 5, background: isActive ? '#eef2ff' : 'white', overflow: 'hidden' }}>
        <input
          type="text"
          placeholder={label}
          value={inputText}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          style={{
            padding: '3px 6px', border: 'none', outline: 'none', fontSize: 11,
            width: Math.max(90, Math.min(160, (inputText || label).length * 7 + 20)),
            background: 'transparent', color: isActive ? '#4338ca' : '#374151',
          }}
        />
        {isActive && (
          <button
            onClick={handleClear}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 13, padding: '0 4px', lineHeight: 1 }}
            title="Clear"
          >
            {'\u00d7'}
          </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 100,
          background: 'white', border: '1px solid #d1d5db', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto',
          minWidth: 160, marginTop: 2,
        }}>
          {filtered.slice(0, 50).map((opt, idx) => (
            <div
              key={`${opt}-${idx}`}
              onClick={() => handleSelect(opt)}
              style={{
                padding: '5px 10px', fontSize: 11, cursor: 'pointer',
                background: opt === value ? '#eef2ff' : 'transparent',
                color: opt === value ? '#4338ca' : '#374151',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseLeave={(e) => (e.currentTarget.style.background = opt === value ? '#eef2ff' : 'transparent')}
            >
              {opt}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: 11, color: '#9ca3af' }}>No matches</div>
          )}
          {filtered.length > 50 && (
            <div style={{ padding: '4px 10px', fontSize: 10, color: '#9ca3af', borderTop: '1px solid #f3f4f6' }}>
              Type more to narrow down ({filtered.length} total)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────

const MAX_NODES_DEFAULT = 2000;
const MAX_NODES_FILTERED = 5000;

// ── Debounce hook ────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

// ── Module-level frontend cache ───────────────────────────────────────────────
// Survives component unmount/remount (navigation away and back).
// Avoids re-downloading the 19 MB graph on every visit within the same session.
const _CACHE_TTL = 30 * 60 * 1000; // 30 minutes
let _graphCache: { data: SchemaGraph; ts: number } | null = null;
let _docsCache:  { data: DocumentInfo[]; ts: number } | null = null;

interface SchemaGraphPageProps {
  onBack?: () => void;
  graphData?: SchemaGraph | null;
  documents?: DocumentInfo[];
  isModal?: boolean;
  /** Node names/ids rendered as dotted "to be created" (CR impact graph,
   *  derived from change_plan action=add). Matched case-insensitively. */
  dashedNames?: string[];
  /** Hide the Document/Module/Database/Schema/Table/Field filter row —
   *  schema-browser filters that mean nothing in an impact-graph embed. */
  hideSchemaFilters?: boolean;
  /** When set, a Maximize button is shown that calls this. */
  onMaximize?: () => void;
}

export default function SchemaGraphPage({ onBack, graphData: initialGraphData, documents: initialDocuments, isModal, dashedNames, hideSchemaFilters, onMaximize }: SchemaGraphPageProps) {
  const [graphData, setGraphData] = useState<SchemaGraph | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [layouting, setLayouting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [cacheStatus, setCacheStatus] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [dbFilter, setDbFilter] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [moduleFilter, setModuleFilter] = useState('');
  const [schemaFilter, setSchemaFilter] = useState('');
  const [documentFilter, setDocumentFilter] = useState('');
  const debouncedDocFilter = useDebounce(documentFilter, 300);
  const [tableFilter, setTableFilter] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');
  const [docData, setDocData] = useState<DocumentInfo[]>([]);
  const [interactionMode, setInteractionMode] = useState<'select' | 'pan'>('select');
  const [labelsOn, setLabelsOn] = useState(true);
  const [listPanelOpen, setListPanelOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<SimNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<SimNode | null>(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
  const [truncatedInfo, setTruncatedInfo] = useState<{ shown: number; total: number } | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const draggedNodeRef = useRef<SimNode | null>(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragMoved = useRef(false);
  const simNodesRef = useRef<SimNode[]>([]);
  const simEdgesRef = useRef<SimEdge[]>([]);
  const nodeMapRef = useRef<Map<string, SimNode>>(new Map());
  const animFrameRef = useRef<number>(0);
  const workerRef = useRef<Worker | null>(null);
  const transformRef = useRef(transform);
  transformRef.current = transform;

  // Load data
  useEffect(() => {
    // If graphData is provided as a prop (e.g., from ModuleGraphModal), use it directly
    if (initialGraphData) {
      setGraphData(initialGraphData);
      setLoading(false);
      setCacheStatus('PROVIDED');
    } else {
      const now = Date.now();

      // ── Schema graph ─────────────────────────────────────────────────────────
      if (_graphCache && now - _graphCache.ts < _CACHE_TTL) {
        // Serve from in-memory frontend cache — instant, no network call
        setGraphData(_graphCache.data);
        setLoading(false);
        setCacheStatus('FRONTEND_CACHE');
      } else {
        getAuthHeaders()
          .then((headers) => fetch('/api/v1/schema/graph', { headers }))
          .then((r) => {
            if (!r.ok) throw new Error(`API ${r.status}`);
            const cacheHeader = r.headers.get('x-cache');
            setCacheStatus(cacheHeader);
            if (cacheHeader === 'STALE' || cacheHeader === 'DISK') setRefreshing(true);
            return r.json();
          })
          .then((data: SchemaGraph) => {
            _graphCache = { data, ts: Date.now() };
            setGraphData(data);
            setLoading(false);
          })
          .catch((apiErr) => {
            console.warn('Neo4j API unavailable, falling back to static JSON:', apiErr.message);
            fetch('/schema_graph.json')
              .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
              .then((data: SchemaGraph) => {
                _graphCache = { data, ts: Date.now() };
                setGraphData(data);
                setLoading(false);
              })
              .catch((err) => { setLoadError(err.message); setLoading(false); });
          });
      }
    }

    // ── Documents ────────────────────────────────────────────────────────────
    if (initialDocuments) {
      setDocData(initialDocuments);
    } else {
      const now = Date.now();
      if (_docsCache && now - _docsCache.ts < _CACHE_TTL) {
        setDocData(_docsCache.data);
      } else {
        docProcessingApi.listDocuments()
          .then((result) => {
            const docs = result.documents || [];
            _docsCache = { data: docs, ts: Date.now() };
            setDocData(docs);
          })
          .catch(() => {});
      }
    }
  }, [initialGraphData, initialDocuments]);

  // Poll for background refresh completion when data is stale
  useEffect(() => {
    if (!refreshing) return;
    const interval = setInterval(() => {
      getAuthHeaders()
        .then((headers) => fetch('/api/v1/schema/graph/status', { headers }))
        .then((r) => r.json())
        .then((status) => {
          if (!status.refreshing && status.cached && !status.stale) {
            // Refresh completed — reload the fresh data
            clearInterval(interval);
            getAuthHeaders()
              .then((headers) => fetch('/api/v1/schema/graph', { headers }))
              .then((r) => {
                if (!r.ok) throw new Error(`API ${r.status}`);
                setCacheStatus(r.headers.get('x-cache'));
                return r.json();
              })
              .then((data: SchemaGraph) => {
                setGraphData(data);
                setRefreshing(false);
              })
              .catch(() => setRefreshing(false));
          }
        })
        .catch(() => {}); // ignore status poll errors
    }, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [refreshing]);

  // Build simulation data
  const { databases, schemas } = useMemo(() => {
    if (!graphData) return { databases: [] as string[], schemas: [] as string[] };
    return extractMetadata(graphData);
  }, [graphData]);

  const modules = useMemo(() => {
    if (!graphData) return [] as string[];
    const mods = new Set<string>();
    for (const n of graphData.nodes) {
      if (n.module) mods.add(n.module);
    }
    return Array.from(mods).sort();
  }, [graphData]);

  const tableNames = useMemo(() => {
    if (!graphData) return [] as string[];
    const names = new Set<string>();
    for (const n of graphData.nodes) {
      if (n.type !== 'Database' && n.type !== 'Module') names.add(n.name);
    }
    return Array.from(names).sort();
  }, [graphData]);

  const fieldNames = useMemo(() => {
    if (!graphData) return [] as string[];
    const names = new Set<string>();
    for (const n of graphData.nodes) {
      if (n.columns) {
        for (const col of n.columns) names.add(col.name);
      }
    }
    return Array.from(names).sort();
  }, [graphData]);

  // Document names and document→module mapping for Document filter
  const documentNames = useMemo(() => {
    return docData.map((d) => d.filename).sort();
  }, [docData]);

  const docModuleMapping = useMemo(() => {
    // Map: document filename → set of linked module names
    const map = new Map<string, Set<string>>();
    for (const d of docData) {
      if (d.linked_modules && d.linked_modules.length > 0) {
        map.set(d.filename, new Set(d.linked_modules));
      }
    }
    return map;
  }, [docData]);

  // Get modules allowed by document filter (if active)
  const docFilterModules = useMemo((): Set<string> | null => {
    if (!debouncedDocFilter.trim()) return null; // no filter
    const q = debouncedDocFilter.toLowerCase();
    const allowedModules = new Set<string>();
    docModuleMapping.forEach((mods: Set<string>, filename: string) => {
      if (filename.toLowerCase().includes(q)) {
        mods.forEach((m: string) => allowedModules.add(m));
      }
    });
    return allowedModules;
  }, [debouncedDocFilter, docModuleMapping]);

  // Auto-select the first database on initial load for faster rendering
  const defaultsApplied = useRef(false);
  useEffect(() => {
    if (!defaultsApplied.current && databases.length > 0) {
      defaultsApplied.current = true;
      setDbFilter(new Set([databases[0]]));
    }
  }, [databases]);

  const dbColorMap = useMemo(() => {
    const map = new Map<string, string>();
    databases.forEach((db, i) => map.set(db, DB_COLORS[i % DB_COLORS.length]));
    return map;
  }, [databases]);

  const dbCounts = useMemo(() => {
    if (!graphData) return new Map<string, number>();
    const counts = new Map<string, number>();
    for (const n of graphData.nodes) {
      if (n.type !== 'Database' && n.database) {
        counts.set(n.database, (counts.get(n.database) || 0) + 1);
      }
    }
    return counts;
  }, [graphData]);

  const typeCounts = useMemo(() => {
    if (!graphData) return new Map<string, number>();
    const counts = new Map<string, number>();
    for (const n of graphData.nodes) {
      counts.set(n.type, (counts.get(n.type) || 0) + 1);
    }
    return counts;
  }, [graphData]);

  // Build and run simulation when data/filters change
  useEffect(() => {
    if (!graphData || !containerRef.current) return;

    const dbIndex = new Map<string, number>();
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    const queryLower = debouncedSearch.toLowerCase();
    const hasActiveFilter = dbFilter.size > 0 || typeFilter.size > 0 || queryLower.length > 0 || moduleFilter !== '' || schemaFilter !== '' || debouncedDocFilter !== '' || tableFilter !== '' || fieldFilter !== '';
    const nodeLimit = hasActiveFilter ? MAX_NODES_FILTERED : MAX_NODES_DEFAULT;

    // Filter nodes
    let filteredNodes = graphData.nodes.filter((n) => {
      if (dbFilter.size > 0) {
        if (n.type === 'Database' && !dbFilter.has(n.name)) return false;
        if (n.type === 'Module') return true; // Module nodes are cross-database, always show
        if (n.type !== 'Database' && n.database && !dbFilter.has(n.database)) return false;
      }
      if (typeFilter.size > 0 && !typeFilter.has(n.type)) return false;
      // Document filter: only show tables/modules whose module is linked to a matching document
      if (docFilterModules) {
        if (n.type === 'Database') return false; // hide databases when filtering by document
        if (n.type === 'Module') return docFilterModules.has(n.name); // show matching module nodes
        if (!n.module || !docFilterModules.has(n.module)) return false;
      }
      if (moduleFilter) {
        const mq = moduleFilter.toLowerCase();
        if (n.type === 'Module') return n.name.toLowerCase() === mq;
        if ((n.module || '').toLowerCase() !== mq) return false;
      }
      if (schemaFilter) {
        const sq = schemaFilter.toLowerCase();
        const nodeSchema = n.database && n.schema ? `${n.database}.${n.schema}` : '';
        if (n.type === 'Database' || n.type === 'Module') return false;
        if (!nodeSchema.toLowerCase().includes(sq)) return false;
      }
      if (tableFilter) {
        const tq = tableFilter.toLowerCase();
        if (n.type === 'Database' || n.type === 'Module') return false;
        if (!n.name.toLowerCase().includes(tq)) return false;
      }
      if (fieldFilter) {
        const fq = fieldFilter.toLowerCase();
        if (n.type === 'Database' || n.type === 'Module') return false;
        if (!n.columns?.some((col) => col.name.toLowerCase().includes(fq))) return false;
      }
      if (queryLower) {
        // Search node name, description, id, and column/field names
        if (n.name.toLowerCase().includes(queryLower)) return true;
        if ((n.description || '').toLowerCase().includes(queryLower)) return true;
        if (n.id.toLowerCase().includes(queryLower)) return true;
        if ((n.module || '').toLowerCase().includes(queryLower)) return true;
        if (n.columns?.some((col) => col.name.toLowerCase().includes(queryLower))) return true;
        return false;
      }
      return true;
    });

    const totalFiltered = filteredNodes.length;
    if (filteredNodes.length > nodeLimit) {
      filteredNodes = filteredNodes.slice(0, nodeLimit);
      setTruncatedInfo({ shown: nodeLimit, total: totalFiltered });
    } else {
      setTruncatedInfo(null);
    }

    const visibleIds = new Set(filteredNodes.map((n) => n.id));

    const dashedLower = (dashedNames ?? []).map((s) => s.toLowerCase());
    const simNodes: SimNode[] = filteredNodes.map((n) => ({
      id: n.id,
      x: 0, y: 0, vx: 0, vy: 0,
      type: n.type,
      name: n.name,
      database: n.database,
      schema: n.schema,
      description: n.description,
      columnCount: n.columnCount,
      // Database-less nodes (CR impact graph: methods, beans, procedures,
      // applications) are colored by TYPE — one color per kind. Nodes from
      // the schema browser keep their per-database color.
      color: n.type === 'Database'
        ? getDbColor(n.name, dbIndex)
        : n.type === 'Module'
        ? '#8b5cf6'
        : n.database
        ? getDbColor(n.database, dbIndex)
        : getTypeColor(n.type),
      radius: n.type === 'Database' ? 18 : n.type === 'Module' ? 14 : 8,
      dashed: dashedLower.length > 0
        && (dashedLower.includes(n.name.toLowerCase())
            || dashedLower.includes(n.id.toLowerCase())),
    }));

    const simEdges: SimEdge[] = graphData.edges
      .filter((e) => visibleIds.has(e.source) && visibleIds.has(e.target))
      .map((e) => ({
        source: e.source,
        target: e.target,
        type: e.type,
        sourceColumn: e.sourceColumn,
        targetColumn: e.targetColumn,
        indirect: e.indirect,
        description: e.description,
      }));

    // Run force simulation in Web Worker
    setLayouting(true);

    // Kill previous worker if still running
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }

    const worker = new Worker(getWorkerUrl());
    workerRef.current = worker;

    worker.onmessage = (evt) => {
      const { positions } = evt.data as { positions: { id: string; x: number; y: number }[] };
      const posMap = new Map(positions.map((p) => [p.id, p]));

      for (const n of simNodes) {
        const pos = posMap.get(n.id);
        if (pos) { n.x = pos.x; n.y = pos.y; }
      }

      // Precompute label placement directions (O(n) via spatial grid)
      precomputeLabelDirs(simNodes);

      // Build spatial grid for fast hit testing
      buildHitGrid(simNodes);

      const nodeMap = new Map<string, SimNode>();
      for (const n of simNodes) nodeMap.set(n.id, n);

      simNodesRef.current = simNodes;
      simEdgesRef.current = simEdges;
      nodeMapRef.current = nodeMap;

      setTransform({ x: 0, y: 0, k: 1 });
      setSelectedNode(null);
      setHoveredNode(null);
      setLayouting(false);

      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
    };

    // Send lightweight data to worker (no color/radius needed for layout)
    const workerNodes = simNodes.map((n) => ({ id: n.id, x: 0, y: 0, vx: 0, vy: 0, radius: n.radius, nameLen: n.name.length }));
    // Adaptive iterations: fewer for large graphs to improve responsiveness
    const nodeCount = workerNodes.length;
    const adaptiveIterations = nodeCount > 1500 ? 80 : nodeCount > 800 ? 100 : nodeCount > 400 ? 120 : 150;
    worker.postMessage({ nodes: workerNodes, edges: simEdges, width, height, iterations: adaptiveIterations });

    return () => {
      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
    };
  }, [graphData, dbFilter, typeFilter, debouncedSearch, moduleFilter, schemaFilter, docFilterModules, debouncedDocFilter, tableFilter, fieldFilter, dashedNames]);

  // Render — only draws when called, no continuous loop
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    drawGraph(
      ctx,
      simNodesRef.current,
      simEdgesRef.current,
      nodeMapRef.current,
      transformRef.current,
      hoveredNode,
      selectedNode,
      width,
      height,
      labelsOn,
    );
  }, [hoveredNode, selectedNode, labelsOn]);

  // Schedule a single redraw via RAF (deduplicates multiple calls per frame)
  const scheduleRedraw = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    animFrameRef.current = requestAnimationFrame(render);
  }, [render]);

  // Redraw when render deps change (hover, selection)
  useEffect(() => {
    scheduleRedraw();
  }, [scheduleRedraw]);

  // Redraw when transform changes (pan/zoom)
  useEffect(() => {
    scheduleRedraw();
  }, [transform, scheduleRedraw]);

  // Resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => scheduleRedraw());
    ro.observe(container);
    return () => ro.disconnect();
  }, [scheduleRedraw]);

  // Hit test: find node under cursor using spatial grid (O(1) average)
  const hitTest = useCallback((clientX: number, clientY: number): SimNode | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const t = transformRef.current;
    const x = (clientX - rect.left - t.x) / t.k;
    const y = (clientY - rect.top - t.y) / t.k;
    return hitTestGrid(x, y);
  }, []);

  // Mouse handlers — with drag support
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const node = hitTest(e.clientX, e.clientY);
    if (node) {
      // Both modes: allow node drag/select
      draggedNodeRef.current = node;
      dragStartPos.current = { x: e.clientX, y: e.clientY };
      dragMoved.current = false;
    } else if (interactionMode === 'pan') {
      // Only allow canvas panning in pan mode
      isPanning.current = true;
      const t = transformRef.current;
      panStart.current = { x: e.clientX - t.x, y: e.clientY - t.y };
    } else {
      // Select mode: click on empty canvas → deselect
      setSelectedNode(null);
    }
  }, [hitTest, interactionMode]);

  // Throttled hover: only update hovered node every ~32ms (30fps) during idle move
  const lastHoverTime = useRef(0);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (draggedNodeRef.current) {
      const t = transformRef.current;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      // A click is a click until the pointer has clearly traveled: the old
      // 4px combined threshold turned slightly-jittery clicks into drags,
      // so mouseup skipped selection and the node "randomly" stopped
      // opening its details. The node also must not move before the
      // threshold passes, or it slides out from under the cursor.
      const totalMove = Math.abs(e.clientX - dragStartPos.current.x) + Math.abs(e.clientY - dragStartPos.current.y);
      if (totalMove > 10) dragMoved.current = true;
      if (dragMoved.current) {
        const newX = (e.clientX - rect.left - t.x) / t.k;
        const newY = (e.clientY - rect.top - t.y) / t.k;
        draggedNodeRef.current.x = newX;
        draggedNodeRef.current.y = newY;
        draggedNodeRef.current.pinned = true;
        scheduleRedraw();
        if (canvasRef.current) canvasRef.current.style.cursor = 'move';
      }
    } else if (isPanning.current) {
      // Update transform ref directly, schedule redraw — avoids React re-render per pixel
      const newX = e.clientX - panStart.current.x;
      const newY = e.clientY - panStart.current.y;
      transformRef.current = { ...transformRef.current, x: newX, y: newY };
      scheduleRedraw();
      if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing';
    } else {
      // Throttle hover detection
      const now = performance.now();
      if (now - lastHoverTime.current < 32) return;
      lastHoverTime.current = now;
      const node = hitTest(e.clientX, e.clientY);
      if (node !== hoveredNode) setHoveredNode(node);
      if (canvasRef.current) {
        canvasRef.current.style.cursor = node ? 'pointer' : (interactionMode === 'pan' ? 'grab' : 'default');
      }
    }
  }, [hitTest, scheduleRedraw, hoveredNode, interactionMode]);

  const handleMouseUp = useCallback(() => {
    if (draggedNodeRef.current) {
      if (!dragMoved.current) {
        // It was a click, not a drag — select the node
        setSelectedNode(draggedNodeRef.current);
      }
      draggedNodeRef.current = null;
    }
    if (isPanning.current) {
      // Sync React state with final pan position
      setTransform({ ...transformRef.current });
    }
    isPanning.current = false;
    if (canvasRef.current) {
      canvasRef.current.style.cursor = interactionMode === 'pan' ? 'grab' : 'default';
    }
  }, [interactionMode]);

  // Double-click: drill down into module
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    const node = hitTest(e.clientX, e.clientY);
    if (!node) return;
    if (node.type === 'Module') {
      // Drill into this module — show its tables
      setModuleFilter(node.name);
      setSelectedNode(null);
    } else if (node.type !== 'Database') {
      // Table node: if it has a module, drill into that module
      const rawNode = graphData?.nodes.find((n) => n.id === node.id);
      if (rawNode?.module) {
        setModuleFilter(rawNode.module);
        setSelectedNode(null);
      }
    }
  }, [hitTest, graphData]);

  // Wheel zoom — native listener with { passive: false } to fix preventDefault error
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const zoom = e.deltaY < 0 ? 1.1 : 0.9;

      setTransform((t) => {
        const newK = Math.max(0.05, Math.min(5, t.k * zoom));
        return {
          k: newK,
          x: mx - (mx - t.x) * (newK / t.k),
          y: my - (my - t.y) * (newK / t.k),
        };
      });
    };

    canvas.addEventListener('wheel', handler, { passive: false });
    return () => canvas.removeEventListener('wheel', handler);
  }, []);

  const toggleDbFilter = useCallback((db: string) => {
    setDbFilter((prev) => {
      const next = new Set(prev);
      if (next.has(db)) {
        next.delete(db);
      } else {
        next.add(db);
      }
      return next;
    });
  }, []);

  const toggleTypeFilter = useCallback((type: string) => {
    setTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const hasAnyFilter = dbFilter.size > 0 || typeFilter.size > 0 || searchQuery.length > 0 || moduleFilter !== '' || schemaFilter !== '' || documentFilter !== '' || tableFilter !== '' || fieldFilter !== '';

  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setDbFilter(new Set());
    setTypeFilter(new Set());
    setModuleFilter('');
    setSchemaFilter('');
    setDocumentFilter('');
    setTableFilter('');
    setFieldFilter('');
  }, []);

  // Fit to view
  const fitToView = useCallback(() => {
    const nodes = simNodesRef.current;
    if (nodes.length === 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of nodes) {
      if (n.x - n.radius < minX) minX = n.x - n.radius;
      if (n.y - n.radius < minY) minY = n.y - n.radius;
      if (n.x + n.radius > maxX) maxX = n.x + n.radius;
      if (n.y + n.radius > maxY) maxY = n.y + n.radius;
    }

    const pad = 40;
    const w = canvas.clientWidth - pad * 2;
    const h = canvas.clientHeight - pad * 2;
    const graphW = maxX - minX || 1;
    const graphH = maxY - minY || 1;
    const k = Math.min(w / graphW, h / graphH, 2);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    setTransform({
      k,
      x: canvas.clientWidth / 2 - cx * k,
      y: canvas.clientHeight / 2 - cy * k,
    });
  }, []);

  // Zoom in/out by button (zoom toward canvas center)
  const zoomBy = useCallback((factor: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cx = canvas.clientWidth / 2;
    const cy = canvas.clientHeight / 2;
    setTransform((t) => {
      const newK = Math.max(0.05, Math.min(5, t.k * factor));
      return {
        k: newK,
        x: cx - (cx - t.x) * (newK / t.k),
        y: cy - (cy - t.y) * (newK / t.k),
      };
    });
  }, []);

  // Get connected nodes for detail panel (deduplicated)
  const connectedNodes = useMemo(() => {
    if (!selectedNode) return [];
    const seen = new Set<string>();
    const connected: SimNode[] = [];
    for (const e of simEdgesRef.current) {
      let targetId: string | null = null;
      if (e.source === selectedNode.id) targetId = e.target;
      else if (e.target === selectedNode.id) targetId = e.source;
      if (targetId && !seen.has(targetId)) {
        seen.add(targetId);
        const n = nodeMapRef.current.get(targetId);
        if (n) connected.push(n);
      }
    }
    return connected;
  }, [selectedNode]);

  // Get raw node for column details
  const selectedRawNode = useMemo(() => {
    if (!selectedNode || !graphData) return undefined;
    return graphData.nodes.find((n) => n.id === selectedNode.id);
  }, [selectedNode, graphData]);

  if (loadError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <p style={{ color: '#dc2626' }}>Failed to load schema graph: {loadError}</p>
        {onBack && !isModal && (
          <button onClick={onBack} style={{ marginTop: 16, padding: '8px 16px', borderRadius: 6, border: '1px solid #d1d5db', cursor: 'pointer' }}>
            Back to Chat
          </button>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        Loading schema graph...
      </div>
    );
  }

  const totalNodes = simNodesRef.current.length;
  const totalEdges = simEdgesRef.current.length;

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative', background: '#fafafa' }}>
      {/* Toolbar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        background: 'white', borderBottom: '1px solid #e5e7eb',
        zIndex: 10,
      }}>
        {/* Top row — wraps rather than clipping: in narrow embeds the
            Pan/zoom controls used to overflow out of sight. */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '4px 16px', gap: 12,
          minHeight: 44, flexWrap: 'wrap',
        }}>
          {onBack && !isModal && (
            <button
              onClick={onBack}
              style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#374151' }}
            >
              {'\u2190'} Back
            </button>
          )}
          <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>Schema Graph</div>
          <div style={{ position: 'relative', width: 180 }}>
            <input
              type="text"
              placeholder="Search tables, columns, modules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ padding: '5px 28px 5px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, width: '100%', color: '#111827', background: 'white' }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, lineHeight: 1 }}
                title="Clear search"
              >
                {'\u00d7'}
              </button>
            )}
          </div>
          <TypeFilterChips typeCounts={typeCounts} filter={typeFilter} onToggle={toggleTypeFilter} />
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => setListPanelOpen((v) => !v)}
              title="Toggle item list panel"
              style={{
                background: listPanelOpen ? '#4f46e5' : 'white',
                color: listPanelOpen ? 'white' : '#374151',
                border: '1px solid #d1d5db', borderRadius: 6,
                padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 500,
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              <span style={{ fontSize: 13 }}>☰</span> List
            </button>
            {hasAnyFilter && (
              <button
                onClick={clearAllFilters}
                style={{
                  background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
                  padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: '#dc2626', fontWeight: 500,
                }}
              >
                Clear All Filters
              </button>
            )}
            <div style={{
              display: 'inline-flex', borderRadius: 6, border: '1px solid #d1d5db',
              overflow: 'hidden', fontSize: 12,
            }}>
              <button
                onClick={() => setInteractionMode('select')}
                title="Select mode: click nodes to select, click canvas to deselect"
                style={{
                  padding: '4px 10px', border: 'none', cursor: 'pointer',
                  background: interactionMode === 'select' ? '#4f46e5' : 'white',
                  color: interactionMode === 'select' ? 'white' : '#374151',
                  fontWeight: 500,
                }}
              >
                Select
              </button>
              <button
                onClick={() => setInteractionMode('pan')}
                title="Pan mode: drag canvas to move around"
                style={{
                  padding: '4px 10px', border: 'none', cursor: 'pointer',
                  borderLeft: '1px solid #d1d5db',
                  background: interactionMode === 'pan' ? '#4f46e5' : 'white',
                  color: interactionMode === 'pan' ? 'white' : '#374151',
                  fontWeight: 500,
                }}
              >
                Pan
              </button>
            </div>
            <button
              onClick={() => zoomBy(1.3)}
              title="Zoom in"
              style={{
                background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
                padding: '4px 8px', fontSize: 14, cursor: 'pointer', color: '#374151', fontWeight: 600,
              }}
            >
              +
            </button>
            <button
              onClick={() => zoomBy(1 / 1.3)}
              title="Zoom out"
              style={{
                background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
                padding: '4px 8px', fontSize: 14, cursor: 'pointer', color: '#374151', fontWeight: 600,
              }}
            >
              −
            </button>
            <button
              onClick={fitToView}
              title="Fit graph to viewport"
              style={{
                background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
                padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#374151',
              }}
            >
              Fit
            </button>
            <button
              onClick={() => setLabelsOn((v) => !v)}
              title={labelsOn ? 'Hide node names' : 'Show node names'}
              style={{
                background: labelsOn ? '#4f46e5' : 'white',
                color: labelsOn ? 'white' : '#374151',
                border: '1px solid #d1d5db', borderRadius: 6,
                padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 500,
              }}
            >
              Labels
            </button>
            {onMaximize && (
              <button
                onClick={onMaximize}
                title="Maximize graph to full screen"
                style={{
                  background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
                  padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#374151', fontWeight: 500,
                }}
              >
                ⛶ Maximize
              </button>
            )}
            <div style={{ fontSize: 11, color: '#9ca3af' }}>
              {totalNodes} nodes &middot; {totalEdges} edges
              {truncatedInfo && (
                <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                  {' '}&middot; {truncatedInfo.shown}/{truncatedInfo.total}
                </span>
              )}
            </div>
          </div>
        </div>
        {/* Filter row — schema-browser filters; hidden in impact-graph
            embeds where type chips are the only meaningful filter. */}
        {!hideSchemaFilters && (
        <div style={{
          display: 'flex', alignItems: 'center', padding: '0 16px', gap: 10, height: 38,
          borderTop: '1px solid #f3f4f6', background: '#fafafa', fontSize: 12,
        }}>
          <span style={{ color: '#6b7280', fontWeight: 500, fontSize: 11 }}>Filters:</span>
          <ComboFilter
            label="Document..."
            options={documentNames}
            value={documentFilter}
            onChange={setDocumentFilter}
          />
          <ComboFilter
            label="Module..."
            options={modules}
            value={moduleFilter}
            onChange={setModuleFilter}
          />
          <ComboFilter
            label="Database..."
            options={databases}
            value={dbFilter.size === 1 ? Array.from(dbFilter)[0] : ''}
            onChange={(val) => setDbFilter(val ? new Set([val]) : new Set())}
          />
          <ComboFilter
            label="Schema..."
            options={schemas}
            value={schemaFilter}
            onChange={setSchemaFilter}
          />
          <ComboFilter
            label="Table..."
            options={tableNames}
            value={tableFilter}
            onChange={setTableFilter}
          />
          <ComboFilter
            label="Field..."
            options={fieldNames}
            value={fieldFilter}
            onChange={setFieldFilter}
          />
          {dbFilter.size > 1 && (
            <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 500 }}>
              {dbFilter.size} databases selected (via legend)
            </span>
          )}
        </div>
        )}
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        style={{
          position: 'absolute', top: 82, left: 0, right: 0, bottom: 28,
          width: '100%', height: 'calc(100% - 110px)', cursor: 'grab',
        }}
      />

      {/* Layouting overlay */}
      {layouting && (
        <div style={{
          position: 'absolute', top: 82, left: 0, right: 0, bottom: 28,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(250,250,250,0.8)', zIndex: 8,
          fontSize: 14, color: '#6b7280',
        }}>
          Laying out graph...
        </div>
      )}

      {/* Legend */}
      <Legend
        dbCounts={dbCounts}
        dbColors={dbColorMap}
        filter={dbFilter}
        onToggle={toggleDbFilter}
      />

      {/* Hover tooltip */}
      {hoveredNode && !selectedNode && (
        <div style={{
          position: 'absolute', bottom: 44, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.8)', color: 'white', padding: '6px 12px',
          borderRadius: 8, fontSize: 12, zIndex: 10, pointerEvents: 'none',
        }}>
          <strong>{hoveredNode.name}</strong>
          {hoveredNode.database && ` \u00b7 ${hoveredNode.database}`}
          {hoveredNode.columnCount != null && ` \u00b7 ${hoveredNode.columnCount} cols`}
        </div>
      )}

      {/* Selected node detail */}
      {selectedNode && (
        <NodeDetail
          node={selectedNode}
          rawNode={selectedRawNode}
          connectedNodes={connectedNodes}
          onClose={() => setSelectedNode(null)}
        />
      )}

      {/* Refresh indicator banner */}
      {refreshing && (
        <div style={{
          position: 'absolute', top: 82, left: '50%', transform: 'translateX(-50%)',
          background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
          padding: '6px 16px', fontSize: 12, color: '#92400e', zIndex: 20,
          display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          <span style={{
            display: 'inline-block', width: 12, height: 12, border: '2px solid #f59e0b',
            borderTopColor: 'transparent', borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          Refreshing graph data from Neo4j... Showing cached data.
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Node list side panel */}
      <SchemaNodeListPanel
        open={listPanelOpen}
        onClose={() => setListPanelOpen(false)}
        graphData={graphData}
        documents={docData}
      />

      {/* Status bar */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 28,
        background: 'white', borderTop: '1px solid #e5e7eb',
        display: 'flex', alignItems: 'center', padding: '0 16px',
        fontSize: 11, color: '#9ca3af', zIndex: 5,
      }}>
        <span>Zoom: {Math.round(transform.k * 100)}%</span>
        <span style={{ marginLeft: 16 }}>
          Displaying {totalNodes} nodes, {totalEdges} relationships
        </span>
        {selectedNode && (
          <span style={{ marginLeft: 16 }}>
            Selected: {selectedNode.name} ({connectedNodes.length} connections)
          </span>
        )}
        {cacheStatus && (
          <span style={{ marginLeft: 16, color: cacheStatus === 'HIT' ? '#10b981' : '#f59e0b' }}>
            Cache: {cacheStatus}
          </span>
        )}
      </div>
    </div>
  );
}
